import FamilySelectionBox from '@/components/FamilySelectionBox';
import SelectCategoryForm from '@/components/SelectCategoryForm';
import TagsInput from '@/components/TagsInput';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import Category from '@/models/Category';
import { EntityType } from '@/models/EntityTypes';
import { Family } from '@/models/Family';
import { LanguageType } from '@/models/LanguageType';
import { Program, ProgramType } from '@/models/Program';
import { resetNavigationToSetScreen } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import { createProgramRequest, getProgramClear, getProgramRequest, updateProgramRequest } from '@/store/program/action';
import React, { useCallback, useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Switch from 'react-switch';
import _ from 'underscore';
import { formInitialState, ProgramForm, programSchema } from './formConfig';
import './style.scss';

interface ConfirmDialogState {
    scormAlert: string;
    leftButtonTitle: string;
    rightButtonTitle: string;
    rightClick: () => void;
    leftClick: () => void;
}

interface TypeData {
    type: string;
    description: string;
    switchVal: boolean;
}

const ProgramInfo: React.FC = () => {
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

    const { strings, identity, success, program, colors } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        personId: state.identity.data.person.id,
        identity: state.identity.data,
        program: state.program.programData,
        success: state.program.success,
        colors: state.appOrganization.data.colors,
    }));

    const [formState, setFormState] = useState(formInitialState);
    const { mainImage, type, name, author, description, locale, categories, tags, families }: ProgramForm = formState;
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [onNextPressed, setOnNextPressed] = useState<boolean>(false);
    const [onSavePressed, setOnSavePressed] = useState<boolean>(false);
    const [originalProgram, setOriginalProgram] = useState<Program>();
    const [isProgramOpen, setIsProgramOpen] = useState<boolean>(true);
    const typeData: TypeData[] = [
        { type: 'READY', description: strings.PROGRAM_STATUS_CLOSE, switchVal: false },
        { type: 'BUILDING', description: strings.PROGRAM_STATUS_OPEN, switchVal: true },
    ];

    useEffect(() => {
        resetNavigationToSetScreen(EntityType.PROGRAM, program?.organizationId, identity.user.admin, history);
    }, [program?.organizationId, identity.user.admin]);

    useEffect(() => {
        return () => {
            dispatch(getProgramClear());
        };
    }, []);

    useEffect(() => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 1,
                nextButtonTitle: strings.NEXT,
                onNext: () => setOnNextPressed(true),
                onPrevious: () => {},
                onSave: () => setOnSavePressed(true),
                saveDisabled: false,
                previousButtonTitle: strings.PREVIOUS,
                saveButtonTitle: program.id ? strings.SAVE_AND_CLOSE : strings.SAVE_TO_DRAFT,
                totalSteps: 2,
                nextDisabled: formIsValid ? false : true,
                previewDisabled: true,
                blockUi: false,
            }),
        );
    }, [dispatch, identity.user.orgId, identity.user.applicationId, strings._LANGUAGE]);

    useEffect(() => {
        if (id) {
            dispatch(getProgramRequest(parseInt(id)));
        } else {
            dispatch(getProgramClear());
        }
    }, [dispatch, id]);

    useEffect(() => {
        programSchema
            .validate(formState)
            .then(() => setFormIsValid(true))
            .catch(() => setFormIsValid(false));
    }, [formState]);

    useEffect(() => {
        disableSaveAndNavButtons(!formIsValid);
    }, [formIsValid, strings._LANGUAGE, dispatch]);

    const handleSaveNext = () => {
        disableSaveAndNavButtons(true);

        dispatch(
            setFloatingCourseActionsConfiguration({
                nextButtonTitle: strings.LOADING,
                blockUi: true,
            } as FloatingCourseActionsBarConfiguration),
        );

        if (program.id) {
            const updatedProgram = {
                ...program,
                name,
                description,
                mainImage,
                author,
                categories,
                tags,
                families,
                type: type as ProgramType,
                moduleId: Constants.LMS_MODULE_ID,
            };

            if (!_.isEqual(originalProgram, updatedProgram)) {
                dispatch(updateProgramRequest(updatedProgram));
            } else {
                redirect();
            }
        } else {
            dispatch(
                createProgramRequest({
                    bizId: `flow_p_${new Date().getTime().toString()}`,
                    name: name,
                    organizationId: families.length > 0 ? null : identity.user.orgId,
                    applicationId: identity.user.applicationId,
                    description,
                    isActive: false,
                    status: 'DRAFT',
                    type,
                    locale: locale as LanguageType,
                    isCourseProgram: false,
                    current: true,
                    moduleId: Constants.LMS_MODULE_ID,
                    author,
                    mainImage: mainImage,
                    categories,
                    tags,
                    families,
                } as Program),
            );
        }
    };

    useEffect(() => {
        if (onSavePressed || onNextPressed) {
            handleSaveNext();
        }
    }, [onSavePressed, onNextPressed]);

    const handleOnChange = useCallback((fieldName: string, value: string | object | null | undefined | Family[] | Category[]) => {
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    }, []);

    useEffect(() => {
        if (program.id) {
            handleOnChange('name', program.name);
            handleOnChange('author', program.author);
            handleOnChange('mainImage', program.mainImage);
            handleOnChange('description', program.description || '');
            handleOnChange('locale', program.locale);
            handleOnChange('tags', program.tags && program.tags.length > 0 ? program.tags : []);
            handleOnChange('families', program.families && program.families.length > 0 ? program.families : []);
            handleOnChange('categories', program.categories || []);
            setIsProgramOpen(program.type === 'BUILDING');

            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveButtonTitle: strings.SAVE_AND_CLOSE,
                    saveDisabled: false,
                    nextDisabled: false,
                } as FloatingCourseActionsBarConfiguration),
            );

            setOriginalProgram(program);
        }
    }, [program]);

    useEffect(() => {
        if (success) {
            redirect();
        }
    }, [success]);

    const redirect = () => {
        if (onSavePressed) {
            history.push('/contents/lms/programs');
        }

        if (onNextPressed) {
            history.push(`/contents/lms/programs/${id || program.id}/contents`);
        }
    };

    const disableSaveAndNavButtons = (disabled: boolean): void => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                saveDisabled: disabled,
                nextDisabled: disabled,
            } as FloatingCourseActionsBarConfiguration),
        );
    };

    useEffect(() => {
        handleOnChange('type', typeData[+isProgramOpen].type);
    }, [isProgramOpen]);

    return (
        <div className="create-program-info position-relative">
            <FamilySelectionBox disabled={!!program.id} familyId={families[0]?.id} handleOnChange={handleOnChange} />
            <span className="create-program-info__warning">* {strings.REQUIRED_FIELDS}</span>
            <div className="input-section required">
                <h1>{strings.HEADER}</h1>
                <Upload
                    onComplete={(e: FileUploadData) => handleOnChange('mainImage', e.key)}
                    onRemove={() => handleOnChange('mainImage', null)}
                    previewFile={{ uri: mainImage, name: mainImage }}
                    typeAccepted="IMAGE"
                />
            </div>
            <div className="input-section">
                <h1>
                    {strings.STATUS}
                    <span className="status-hint">
                        <OverlayTrigger overlay={<Tooltip id="status-hint"> {strings.PROGRAM_STATUS_HINT}</Tooltip>} placement="bottom">
                            <i className="material-icons-outlined">{Constants.icons.ERROR_OUTLINE}</i>
                        </OverlayTrigger>
                    </span>
                </h1>
                <div className="type-input">
                    <Switch
                        checked={!isProgramOpen}
                        checkedIcon={false}
                        disabled={program.status === 'PUBLISHED' && program.type === 'READY'}
                        handleDiameter={8}
                        height={17}
                        offColor="#d1d1d1"
                        offHandleColor="#b3b3b3"
                        onChange={() => setIsProgramOpen(!isProgramOpen)}
                        onClick={() => setIsProgramOpen(!isProgramOpen)}
                        onColor={colors[0]}
                        onHandleColor={colors[0]}
                        uncheckedIcon={false}
                        width={40}
                    />
                    <p>{typeData[+isProgramOpen].description}</p>
                </div>
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.TITLE}</h1>
                <input
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('name', e.target.value)}
                    placeholder={strings.TYPE_TITLE}
                    type="text"
                    value={name}
                />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.AUTHOR}</h1>
                <input
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('author', e.target.value)}
                    placeholder={strings.TYPE_AUTHOR}
                    type="text"
                    value={author}
                />
            </div>
            <div className="input-section">
                <h1>{strings.PROGRAM_DESCRIPTION}</h1>
                <textarea
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleOnChange('description', e.target.value)}
                    placeholder={strings.INSERT_PROGRAM_DESCRIPTION_TO_HELP}
                    rows={3}
                    value={description}
                />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.SELECT_LANGUAGE}</h1>
                <div className="container button-group-content-type">
                    <button
                        className={`btn btn-light ${locale === 'pt' ? 'active' : ''}`}
                        disabled={program.id ? true : false}
                        onClick={() => handleOnChange('locale', 'pt')}
                        type="button"
                    >
                        <span>{strings.LANGUAGE_PT}</span>
                    </button>

                    <button
                        className={`btn btn-light ${locale === 'en' ? 'active' : ''}`}
                        disabled={program.id ? true : false}
                        onClick={() => handleOnChange('locale', 'en')}
                        type="button"
                    >
                        <span>{strings.LANGUAGE_EN}</span>
                    </button>
                </div>
            </div>
            <div className="input-section required">
                <SelectCategoryForm categoriesForm={categories} familyId={families[0]?.id} handleOnChange={handleOnChange} />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.TAGS}</h1>
                <div className="tags">
                    <TagsInput selectedTags={tags} setTags={handleOnChange} />
                </div>
            </div>
        </div>
    );
};

export default ProgramInfo;
